import { WaitForQueries } from '@graphcommerce/ecommerce-ui'
import { PageOptions } from '@graphcommerce/framer-next-pages'
import { hygraphPageContent, HygraphPagesQuery } from '@graphcommerce/graphcms-ui'
import {
  ApolloCartErrorAlert,
  CartStartCheckout,
  CartStartCheckoutLinkOrButton,
  EmptyCart,
  useCartQuery,
} from '@graphcommerce/magento-cart'
import { CartPageDocument } from '@graphcommerce/magento-cart-checkout'
import { CouponAccordion } from '@graphcommerce/magento-cart-coupon'
import { CartItems } from '@graphcommerce/magento-cart-items'
import { ConfigurableCartItem } from '@graphcommerce/magento-product-configurable'
import { Money, PageMeta, StoreConfigDocument } from '@graphcommerce/magento-store'
import {
  GetStaticProps,
  LayoutTitle,
  LayoutOverlayHeader,
  FullPageMessage,
} from '@graphcommerce/next-ui'
import { i18n } from '@lingui/core'
import { Trans } from '@lingui/react'
import { Container, Box, CircularProgress } from '@mui/material'
import { LayoutOverlay, LayoutOverlayProps, RowRenderer } from '../components'
import { CartFreeShippingAlert } from '../components/Cart/CartFreeShippingAlert'
import { CartItem } from '../components/Cart/CartItem'
import { CartTotals } from '../components/Cart/CartTotals'
import CustomCartIcon from '../components/Icons/CustomCartIcon.svg'
import { Stepper } from '../components/Stepper/Stepper'
import { graphqlSharedClient, graphqlSsrClient } from '../lib/graphql/graphqlSsrClient'
import { usePromotions } from '../lib/promotions/usePromotions'

type CartPageProps = HygraphPagesQuery
type Props = Record<string, unknown>
type GetPageStaticProps = GetStaticProps<LayoutOverlayProps, Props>

function CartPage(props: CartPageProps) {
  const { pages } = props
  const cartData = useCartQuery(CartPageDocument, {
    errorPolicy: 'all',
    fetchPolicy: 'cache-and-network',
  })
  const page = pages?.[0]
  const { error, data } = cartData
  const cart = data?.cart
  const hasError = Boolean(error)
  const hasItems =
    (cart?.total_quantity ?? 0) > 0 && typeof cart?.prices?.grand_total?.value !== 'undefined'

  const promotions = usePromotions({ items: cart?.items })
  const discountValue = cart?.prices?.discounts?.[0]?.amount?.value
  const subtotalIncludingTax = cart?.prices?.subtotal_including_tax?.value

  const shippingSubtotal =
    discountValue && subtotalIncludingTax
      ? subtotalIncludingTax - discountValue
      : subtotalIncludingTax

  return (
    <>
      <PageMeta
        title={i18n._(/* i18n */ 'Cart ({0})', { 0: cart?.total_quantity ?? 0 })}
        metaRobots={['noindex']}
      />
      <LayoutOverlayHeader
        switchPoint={0}
        primary={<CartStartCheckoutLinkOrButton {...cart} disabled={hasError} />}
        divider={hasItems && <Container />}
      >
        <LayoutTitle size='small' component='span' icon={hasItems ? CustomCartIcon : undefined}>
          {hasItems ? (
            <Box
              sx={(theme) => ({
                display: 'flex',
                alignItems: 'center',
                '& .price': {
                  fontSize: theme.typography.h3,
                  display: 'inline-block',
                  lineHeight: 'normal',
                  marginLeft: theme.spacings.xxs,
                },
              })}
            >
              <Trans id='Total <0/>' components={{ 0: <Money {...cart?.prices?.grand_total} /> }} />
            </Box>
          ) : (
            <Trans id='Cart' />
          )}
        </LayoutTitle>
      </LayoutOverlayHeader>
      <WaitForQueries
        waitFor={cartData}
        fallback={
          <FullPageMessage
            icon={<CircularProgress />}
            title={<Trans id='Loading your data' />}
            sx={{ maxWidth: '100vw' }}
          >
            <Trans id='This may take a second' />
          </FullPageMessage>
        }
      >
        <Container
          maxWidth='md'
          sx={(theme) => ({
            width: 800,
            maxWidth: '100vw',
            [theme.breakpoints.up('md')]: {
              overflowY: 'auto',
              maxHeight: hasItems ? `calc(100vh - ${theme.appShell.appBarHeightMd})` : 'none',
            },
          })}
        >
          <>
            {hasItems ? (
              <>
                <Stepper
                  currentStep={1}
                  steps={3}
                  sx={(theme) => ({ mt: theme.spacings.sm, mb: theme.spacings.xxs })}
                />
                <Box sx={(theme) => ({ mt: theme.spacings.lg })}>
                  <CartItems
                    items={cart?.items}
                    id={cart?.id ?? ''}
                    key='cart'
                    renderer={{
                      BundleCartItem: (itemProps) => (
                        <CartItem {...itemProps} promotions={promotions} />
                      ),
                      ConfigurableCartItem,
                      DownloadableCartItem: (itemProps) => (
                        <CartItem {...itemProps} promotions={promotions} />
                      ),
                      SimpleCartItem: (itemProps) => (
                        <CartItem {...itemProps} promotions={promotions} />
                      ),
                      VirtualCartItem: (itemProps) => (
                        <CartItem {...itemProps} promotions={promotions} />
                      ),
                      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                      // @ts-ignore GiftCardProduct is only available in Commerce
                      GiftCardCartItem: CartItem,
                    }}
                  />
                  <CouponAccordion key='couponform' sx={(theme) => ({ mb: theme.spacings.sm })} />
                  <CartFreeShippingAlert
                    sx={(theme) => ({ mb: theme.spacings.sm })}
                    shippingSubtotal={shippingSubtotal}
                  />
                  <CartTotals sx={{ typography: 'body1' }} />
                  <ApolloCartErrorAlert error={error} />
                  {page && (
                    <Box sx={(theme) => ({ mt: theme.spacings.md })}>
                      <RowRenderer content={page?.content} />
                    </Box>
                  )}
                  <Box key='checkout-button'>
                    <CartStartCheckout {...cart} disabled={hasError} />
                  </Box>
                </Box>
              </>
            ) : (
              <EmptyCart>{error && <ApolloCartErrorAlert error={error} />}</EmptyCart>
            )}
          </>
        </Container>
      </WaitForQueries>
    </>
  )
}

const pageOptions: PageOptions<LayoutOverlayProps> = {
  overlayGroup: 'cart',
  Layout: LayoutOverlay,
  layoutProps: { variantMd: 'right', variantSm: 'bottom' },
}
CartPage.pageOptions = pageOptions

export default CartPage

export const getStaticProps: GetPageStaticProps = async ({ locale }) => {
  const client = graphqlSharedClient(locale)
  const staticClient = graphqlSsrClient(locale)
  const conf = client.query({ query: StoreConfigDocument })

  const pages = hygraphPageContent(staticClient, 'cart')

  return {
    props: {
      ...(await pages).data,
      apolloState: await conf.then(() => client.cache.extract()),
    },
  }
}
